import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { trackPageView, setAdminExcluded } from "../lib/analytics";

const SESSION_KEY = "last_analytics_sid";
const SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function getOrCreateSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.sid && parsed.exp > Date.now()) {
        // Sliding expiry — every page view extends the cookie.
        parsed.exp = Date.now() + SESSION_TTL_MS;
        localStorage.setItem(SESSION_KEY, JSON.stringify(parsed));
        return parsed.sid;
      }
    }
  } catch (_) {
    /* localStorage blocked — fall through and just generate per-visit */
  }
  const sid = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  try {
    localStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ sid, exp: Date.now() + SESSION_TTL_MS }),
    );
  } catch (_) { /* ignore */ }
  return sid;
}

/**
 * Fires a single POST per route change. Mounted once at app root.
 * Failures are swallowed because analytics must never break navigation.
 *
 * Why a component, not a hook somewhere?  We need a stable observer that
 * lives next to the router but doesn't pull in any UI tree, so it can
 * safely live inside `<BrowserRouter>` without re-mounting on layout
 * changes.
 */
export default function AnalyticsTracker() {
  const location = useLocation();
  const { user, loading } = useAuth();
  const isAdmin = !!(user && user.role === "admin");

  // Propagate admin status to lib/analytics.js (and from there to
  // index.html's `window.__gaSetAdminStatus`) the instant auth resolves,
  // and again on every login/logout — this is what actually gates GA4's
  // initialisation and clears any already-queued page view the moment an
  // admin is identified. Safe to re-run with the same value; both the
  // module flag and `window.__gaSetAdminStatus` are idempotent.
  useEffect(() => {
    if (loading) return; // wait for auth to resolve before deciding anything
    setAdminExcluded(isAdmin);
  }, [loading, isAdmin]);

  // GA4 page views must fire exactly once per real route change, and NEVER
  // at all for a confirmed admin, site-wide (not just /admin pages). We
  // can't decide that from `location.pathname` alone — we need to know
  // `loading`/`user` too — but naively depending on them (like the
  // Emergent effect below) re-fires on every auth-state change, which is
  // exactly the duplicate-page_view bug fixed previously. `lastDecidedPath`
  // remembers which pathname we've already made a send/skip decision for,
  // so an auth-state change alone (same path) never re-triggers a decision
  // — only a genuine path change does. While `loading` is true we don't
  // call `trackPageView()` at all (not even to queue it), so a delayed
  // GA4 init can never flush a page view we hadn't yet decided belongs to
  // an admin — it simply doesn't exist until we've confirmed non-admin.
  // (`trackPageView()` itself also checks the admin flag directly, so this
  // is belt-and-suspenders, not the only guard.)
  const lastDecidedPath = useRef(null);
  useEffect(() => {
    if (loading) return; // auth not resolved yet — wait before deciding
    const path = location.pathname;
    if (lastDecidedPath.current === path) return; // already decided for this path
    lastDecidedPath.current = path;
    if (isAdmin) return; // never send GA4 for admins
    trackPageView();
  }, [location.pathname, loading, isAdmin]);

  // Fires a single POST per route change, same invariant as the GA4
  // effect above. Previously this depended on raw `location.pathname,
  // loading, user` with no "already decided for this path" guard, so a
  // `user` object reference change on the SAME path (e.g. re-fetching
  // /auth/me returns a fresh object after the fire, or login/logout state
  // flips without navigating) could re-run the effect and fire a second
  // /analytics/track call for a pageview we'd already tracked — inflating
  // same-session/same-path event pairs that feed the rapid-fire signal.
  const lastTrackedPath = useRef(null);
  useEffect(() => {
    // Wait until we know whether the visitor is the admin before deciding
    // anything — and before marking the path as "decided" below.
    if (loading) return;
    const path = location.pathname || "/";
    if (lastTrackedPath.current === path) return; // already decided (sent or skipped) for this path
    lastTrackedPath.current = path;

    // Don't track admin pages or the splash gate — they're internal.
    if (path.startsWith("/admin")) return;

    // Old pre-slug bookmarks/shared links land on /post/{uuid} and
    // PostDetail.jsx redirects (replace) to /post/{slug} almost
    // instantly — tracking this transient uuid pathname double-counts
    // the pageview and pollutes "Top Pages" with uuid rows forever.
    // Skip it; the redirect's own pathname change tracks the real hit.
    const UUID_POST_RE = /^\/post\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/?$/i;
    if (UUID_POST_RE.test(path)) return;

    if (user && user.role === "admin") return;

    const session = getOrCreateSession();
    // referrer is the page the visitor came from. On a real visit it's
    // an external site; on internal navigation it's our own host (we
    // strip those server-side and label them "direct").
    const referrer = typeof document !== "undefined" ? document.referrer || "" : "";
    // True when the visit was launched from the installed PWA's home-screen
    // icon. Chrome / Android / desktop browsers report this via the
    // `display-mode: standalone` media query; iOS Safari uses the legacy
    // `navigator.standalone` boolean.
    const isStandalone =
      (typeof window !== "undefined" &&
        ((window.matchMedia &&
          window.matchMedia("(display-mode: standalone)").matches) ||
          window.navigator?.standalone === true)) ||
      false;

    api
      .post("/analytics/track", {
        path,
        referrer,
        session,
        is_standalone: isStandalone,
      })
      .catch(() => { /* silent */ });
  }, [location.pathname, loading, user]);

  return null;
}
